import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Briefcase,
  Send,
  CheckCircle2,
  Mail,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { BRAND, JOBS } from "@/lib/constants";
import { submitApplication } from "@/lib/api";

export default function Careers() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    resume_link: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#apply") {
      const el = document.getElementById("apply");
      if (el)
        setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          150
        );
    }
  }, [location]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startApply = (title) => {
    setForm((f) => ({ ...f, position: title }));
    const el = document.getElementById("apply");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    const cleaned = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
      position: form.position.trim(),
      resume_link: form.resume_link.trim() || null,
      message: form.message.trim() || null,
    };
    try {
      // 1) Save to MongoDB (source of truth)
      await submitApplication(cleaned);

      // 2) Fire off an email to info@mdsupplywellness.com with the full application
      const subject = `New Application — ${cleaned.position} — ${cleaned.name}`;
      const body = [
        `New job application submitted via mdsupplywellness.com`,
        ``,
        `Position:     ${cleaned.position}`,
        `Name:         ${cleaned.name}`,
        `Email:        ${cleaned.email}`,
        `Phone:        ${cleaned.phone}`,
        `Resume link:  ${cleaned.resume_link || "(not provided)"}`,
        ``,
        `Why this role:`,
        cleaned.message || "(none)",
        ``,
        `— Submitted from MD Supply Wellness careers page`,
      ].join("\n");
      const mailto = `mailto:${BRAND.careersEmail}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`;
      // Open the user's email client so the message is actually sent to us.
      window.location.href = mailto;

      setSuccess(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        position: "",
        resume_link: "",
        message: "",
      });
      toast.success("Application received!", {
        description: `We also opened your email to send a copy to ${BRAND.careersEmail}.`,
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="careers-page">
      {/* HERO */}
      <section className="py-14 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#28C0D8]/30 bg-[#28C0D8]/5 px-4 py-1.5 text-xs font-medium tracking-wide text-[#28C0D8]">
              <Sparkles className="h-3 w-3" />
              Now hiring · Bethesda, MD
            </div>
            <h1 className="mt-6 font-display text-[32px] leading-[1.1] sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
              Build something meaningful with{" "}
              <span className="text-[#28C0D8]">us.</span>
            </h1>
            <p className="mt-6 text-[17px] leading-relaxed text-[#94A3B8]">
              We&apos;re assembling the team that will open Bethesda&apos;s
              most trusted cannabis wellness dispensary — hiring for four core
              roles ahead of launch. Compliance-first, education-forward, and
              rooted in the Montgomery County community.
            </p>
            <div className="mt-8 flex flex-col items-start sm:flex-row sm:items-center gap-3">
              <a
                href="#apply"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById("apply")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-teal-glow inline-flex items-center justify-center gap-2 rounded-full bg-[#28C0D8] px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC]"
                data-testid="careers-hero-apply-btn"
              >
                Apply Now <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={`mailto:${BRAND.careersEmail}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm md:px-6 md:py-3.5 md:text-[15px] font-medium text-white hover:bg-white/5"
              >
                <Mail className="h-4 w-4" /> {BRAND.careersEmail}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* POSITIONS */}
      <section className="pb-14 md:pb-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-14">
          <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold mb-4">
            Open Positions
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-white mb-10">
            Four roles. One mission.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {JOBS.map((job, i) => (
              <div
                key={job.slug}
                id={job.slug}
                className="group rounded-2xl border border-white/10 bg-[#13161C] p-6 md:p-8 hover:border-[#28C0D8]/50 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                data-testid={`careers-job-card-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="h-10 w-10 rounded-xl bg-[#28C0D8]/10 text-[#28C0D8] flex items-center justify-center mb-4">
                      <Briefcase className="h-5 w-5" />
                    </div>
                    <h3 className="font-display text-xl md:text-2xl font-semibold text-white tracking-tight">
                      {job.title}
                    </h3>
                    <p className="mt-1 text-[11px] md:text-xs uppercase tracking-[0.2em] text-[#28C0D8]">
                      {job.type}
                    </p>
                  </div>
                </div>
                <ul className="mt-5 md:mt-6 space-y-3 text-sm text-[#CBD5E1] flex-1">
                  {job.bullets.map((b) => (
                    <li key={b} className="flex gap-3">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#28C0D8] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 md:mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => startApply(job.title)}
                    className="btn-teal-glow inline-flex items-center gap-2 rounded-full bg-[#28C0D8] px-4 py-2 md:px-5 md:py-2.5 text-sm font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC]"
                    data-testid={`careers-apply-btn-${i}`}
                  >
                    Apply for this role <ArrowRight className="h-4 w-4" />
                  </button>
                  <a
                    href={`mailto:${BRAND.careersEmail}?subject=${encodeURIComponent(
                      "Application — " + job.title
                    )}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 md:px-5 md:py-2.5 text-sm text-white/80 hover:text-white hover:bg-white/5"
                    data-testid={`careers-mailto-${i}`}
                  >
                    <Mail className="h-4 w-4" /> Email instead
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section
        id="apply"
        className="py-14 md:py-24 border-t border-white/5 bg-[#0A0B0E] scroll-mt-24"
      >
        <div className="max-w-[880px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
              Apply
            </p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              Easy application.
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] text-[#94A3B8] max-w-xl mx-auto">
              Two minutes, no account needed. We&apos;ll follow up by email as
              hiring opens.
            </p>
          </div>

          <div className="rounded-3xl border border-white/10 bg-[#13161C] p-6 md:p-10">
            {success ? (
              <div
                className="flex items-start gap-4 rounded-xl border border-[#28C0D8]/40 bg-[#28C0D8]/10 p-5"
                data-testid="application-success"
              >
                <CheckCircle2 className="h-6 w-6 text-[#28C0D8] shrink-0" />
                <div>
                  <p className="font-display text-lg font-semibold text-white">
                    Application received.
                  </p>
                  <p className="mt-1 text-sm text-white/80">
                    Thanks for applying — we&apos;ll be in touch as we start
                    scheduling interviews. In the meantime, feel free to email
                    additional materials to{" "}
                    <a
                      href={`mailto:${BRAND.careersEmail}`}
                      className="text-[#28C0D8] hover:underline"
                    >
                      {BRAND.careersEmail}
                    </a>
                    .
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    className="mt-4 text-sm text-[#28C0D8] hover:underline"
                  >
                    Apply for another role
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                data-testid="application-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
                      Full Name
                    </span>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Doe"
                      className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-[15px] text-white placeholder-white/30 focus:border-[#28C0D8] focus:outline-none focus:ring-2 focus:ring-[#28C0D8]/30"
                      data-testid="application-name-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
                      Email
                    </span>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-[15px] text-white placeholder-white/30 focus:border-[#28C0D8] focus:outline-none focus:ring-2 focus:ring-[#28C0D8]/30"
                      data-testid="application-email-input"
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
                      Phone
                    </span>
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(301) 555-0100"
                      className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-[15px] text-white placeholder-white/30 focus:border-[#28C0D8] focus:outline-none focus:ring-2 focus:ring-[#28C0D8]/30"
                      data-testid="application-phone-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
                      Position
                    </span>
                    <select
                      required
                      value={form.position}
                      onChange={update("position")}
                      className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-[15px] text-white focus:border-[#28C0D8] focus:outline-none focus:ring-2 focus:ring-[#28C0D8]/30"
                      data-testid="application-position-select"
                    >
                      <option value="" disabled>
                        Select a role…
                      </option>
                      {JOBS.map((j) => (
                        <option key={j.slug} value={j.title}>
                          {j.title}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="block">
                  <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
                    Resume link{" "}
                    <span className="lowercase text-[#64748B]">
                      (Google Drive, LinkedIn, Dropbox — optional)
                    </span>
                  </span>
                  <input
                    type="url"
                    value={form.resume_link}
                    onChange={update("resume_link")}
                    placeholder="https://…"
                    className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-[15px] text-white placeholder-white/30 focus:border-[#28C0D8] focus:outline-none focus:ring-2 focus:ring-[#28C0D8]/30"
                    data-testid="application-resume-input"
                  />
                </label>

                <label className="block">
                  <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-semibold">
                    Why this role?{" "}
                    <span className="lowercase text-[#64748B]">(optional)</span>
                  </span>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="A short note about your background and why you want to join MD Supply Wellness."
                    className="mt-2 w-full rounded-lg border border-white/10 bg-[#0A0B0E] px-4 py-3 text-[15px] text-white placeholder-white/30 focus:border-[#28C0D8] focus:outline-none focus:ring-2 focus:ring-[#28C0D8]/30 resize-y"
                    data-testid="application-message-input"
                  />
                </label>

                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-xs text-[#64748B] max-w-xs">
                    All positions require 21+ and background screening per
                    Maryland regulations.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-teal-glow inline-flex items-center gap-2 rounded-full bg-[#28C0D8] px-5 py-2.5 text-sm md:px-6 md:py-3 md:text-[15px] font-semibold text-[#0A0B0E] hover:bg-[#1FA8BC] disabled:opacity-60"
                    data-testid="application-submit-btn"
                  >
                    {loading ? "Sending…" : "Submit application"}
                    {!loading && <Send className="h-4 w-4" />}
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="mt-8 text-center text-[13px] text-[#64748B]">
            Prefer email? Send resume &amp; cover note to{" "}
            <a
              href={`mailto:${BRAND.careersEmail}`}
              className="text-[#28C0D8] hover:underline"
            >
              {BRAND.careersEmail}
            </a>{" "}
            with the role in the subject line. MD Supply Wellness is an equal
            opportunity employer.
          </p>
        </div>
      </section>
    </div>
  );
}
